import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';


import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';

import { ComplianceService } from '@zeta-services';
import { useUserDetailsStore, enqueueSnackbar } from '@zeta-core';
import { useNavigate } from 'react-router';
import { RouterLinks } from '../../../app-route-paths';

import { ComplianceReview } from '../agents/types/compliance';


export const ComplianceReviewsScreen = () => {
  const [reviews, setReviews] = useState<ComplianceReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const user = useUserDetailsStore((state) => state.user);
  const { id: advisorId } = useUserDetailsStore((s) => s.user);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);


  useEffect(() => {
    if (advisorId) {
      loadReviews();
    }
  }, [advisorId]);
  // --------------------------------------------------
  // Load reviews
  // --------------------------------------------------
  const loadReviews = async () => {
  if (!advisorId) {
    setError('Advisor not found. Please sign in again.');
    setLoading(false);
    return;
  }

  try {
    setRefreshing(true);
    setLoading(true);

    const res = await ComplianceService().fetchReviews({ advisorId });
    setReviews(res.data || []);
    setError(null);
  } catch {
    setError('Failed to load compliance reviews.');
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};


  // --------------------------------------------------
  // Delete review
  // --------------------------------------------------
  const handleDelete = async (reviewId: string) => {
    try {
      await ComplianceService().deleteReview({ reviewId });
      enqueueSnackbar('Compliance review deleted.', { variant: 'success' });

      // Optimistic refresh
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch {
      enqueueSnackbar('Failed to delete review.', { variant: 'error' });
    }
  };

  return (
    <Box p={4}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Compliance Reviews
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Regulatory document review history
          </Typography>
        </Box>

          <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadReviews}
            disabled={loading || refreshing}
            sx={{
              borderRadius: 2,
              height: 40,
              px: 2,
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(RouterLinks.complianceNewReview)}
            disabled={!advisorId}
            sx={{
              borderRadius: 2,
              height: 40,
              px: 2,
            }}
          >
            Create Review
          </Button>
        </Stack>

      </Stack>

      {/* Content */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          {loading && (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          )}

          {!loading && error && (
            <Typography color="error" textAlign="center">
              {error}
            </Typography>
          )}

          {!loading && !error && reviews.length === 0 && (
            <Stack alignItems="center" spacing={2} py={6}>
              <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="h6">
                No compliance reviews yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a regulated document to create your first review.
              </Typography>
            </Stack>
          )}

          {!loading && !error && reviews.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Document</strong></TableCell>
                    <TableCell><strong>Regulation</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reviews.map((review) => (
                    <TableRow
                      key={review.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          RouterLinks.complianceReviewDetails
                        )
                      }
                    >
                      <TableCell>{review.fileName}</TableCell>
                      <TableCell>{review.regulation}</TableCell>
                      <TableCell>
                        <Chip
                          label={review.runStatus}
                          color={
                            review.runStatus === 'SUCCESS'
                              ? 'success'
                              : 'error'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(review.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation(); // 🚫 prevent row click
                            navigate(RouterLinks.complianceReviewDetails, {state: {reviewId: review.id}} );
                          }}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation(); // 🚫 prevent row click
                            setDeleteTargetId(review.id); // open confirm modal
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <Dialog
          open={!!deleteTargetId}
          onClose={() => setDeleteTargetId(null)}
        >
          <DialogTitle>Delete compliance review?</DialogTitle>

          <DialogContent>
            <DialogContentText>
              This action cannot be undone. The compliance review and its
              results will be permanently deleted.
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setDeleteTargetId(null)}
              color="inherit"
            >
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={async () => {
                if (!deleteTargetId) return;
                await handleDelete(deleteTargetId);
                setDeleteTargetId(null);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

    </Box>
  );
};
